import { getDateComponents } from "@b/helpers/time";
import { formatMinorToMajorUnits } from "@h/currency";

export class EcommerceShared {
  static getCurrencyCode = (props) => {
    if (props.reservationCurrency) {
      return props.reservationCurrency;
    }

    if (Object.values(props.priceLevels || {}).length > 0) {
      return props.priceLevels[Object.keys(props.priceLevels)[0]].price.currency;
    }
    return 'USD';
  }

  static getBaseDataLayer = (props) => ({
    'ecommerce': {
      'currencyCode': this.getCurrencyCode(props),
    }
  })

  static getCalendarDateSelectedLayer = (date, props) => {
    const dataLayer = this.getBaseDataLayer(props);

    if (!date) return;

    const { dayName, dayNumber, monthName, year } = getDateComponents(new Date(date));

    dataLayer.event = 'calendarDateSelection';
    dataLayer.ecommerce = {
      event_id: props.mainEvent?.event_id,
      event_name: props.mainEvent?.name,
      event_day: dayName,
      event_date: date,
      event_month: monthName,
      event_year: year,
    };

    return dataLayer;
  }

  static getReportCalendarTimeSelectedLayer = (event, props) => {
    const dataLayer = this.getBaseDataLayer(props);

    if (!event.start) return;

    const { dayName, dayNumber, monthName, year, time } = getDateComponents(new Date(event.start));

    dataLayer.event = 'calendarTimeSelection';
    dataLayer.ecommerce = {
      event_id: event.event_id,
      event_name: event.name,
      event_day: dayName,
      event_date: dayNumber,
      event_month: monthName,
      event_year: year,
      event_time: time,
      event_timezone: event.timezone,
    }

    return dataLayer
  }

  static getReportDateChangedLayer = (props) => {
    const dataLayer = this.getBaseDataLayer(props);

    dataLayer.event = 'calendarChangeDateSelection';
    dataLayer.ecommerce = {
      event_id: props.mainEvent?.event_id,
      event_name: props.mainEvent?.name,
    }

    return dataLayer;
  }

  static getCalendarSource = (props) => {
    if (props.currentRouteName === 'ticketPicker' && !props.overlayVisible) {
      return 'listing';
    }

    return props.currentRouteName;
  }

  static getCalendarViewLayer = ({ numberOfMonths, month, source } = {}, props) => {
    const dataLayer = this.getBaseDataLayer(props);

    const getCalendarData = () => {
      if (!month) {
        return {};
      }

      const { monthName, year } = getDateComponents(new Date(month));

      return {
        calendar_month: monthName,
        calendar_year: year,
      };
    }

    const calendarSource = source || this.getCalendarSource(props);

    const additionalData = {
      calendar_on_listing: props.calendarOnListing,
    }

    dataLayer.event = 'calendarView';
    dataLayer.ecommerce = {
      event_id: props.mainEvent?.event_id,
      event_name: props.mainEvent?.name,
      calendar_display_type: props.calendarDisplayType,
      calendar_source: calendarSource,
      ...getCalendarData(),
      ...(numberOfMonths ? { calendar_number_of_months: numberOfMonths } : {}),
      ...(!props.overlayVisible ? additionalData : {}),
    };

    return dataLayer;
  }

  static getTargetObject = (type, props) => {
    if (type === 'ticket') {
      return props.priceLevels;
    }

    if (type === 'product') {
      return props.products;
    }

    if (type === 'bundle') {
      return props.bundles;
    }
  }

  static getEventId = (itemId, type, props) => {
    const targetObject = this.getTargetObject(type, props);

    if (!targetObject) {
      console.warn(`Target object for type ${type} not found. Returning undefined for eventId.`);
      return undefined;
    }

    return targetObject[itemId]?.eventId;
  }

  static getTicketData = (priceLevelId, value, props) => {
    const priceLevel = props.priceLevels[priceLevelId];

    if (!priceLevel) {
      console.warn(`Price level with id ${id} not found. Handling increment action without it.`);
      return;
    }

    const valuePropertyName = typeof value !== 'undefined' ? 'ticket_quantity_selected' : 'ticket_quantity';

    return {
      ticket_id: priceLevel.price_level_id,
      ticket_name: priceLevel.name,
      ticket_price: formatMinorToMajorUnits({ amount: priceLevel.price.price, currency: priceLevel.price.currency }),
      ticket_currency: priceLevel.price.currency,
      [valuePropertyName]: typeof value !== 'undefined' ? value : 1,
    }
  }

  static getProductData = (productId, value, props) => {
    const product = props.products[productId];

    if (!product) {
      console.warn(`Product with id ${productId} not found. Handling increment action without it.`);
      return;
    }

    const valuePropertyName = typeof value !== 'undefined' ? 'product_quantity_selected' : 'product_quantity';

    return {
      product_id: product.product_id,
      product_name: product.name,
      product_price: formatMinorToMajorUnits({ amount: product.price.amount, currency: product.price.currency }),
      product_currency: product.price.currency,
      [valuePropertyName]: typeof value !== 'undefined' ? value : 1,
    }
  }

  static getBundleData = (bundleId, value, props) => {
    const bundle = props.bundles[bundleId];

    if (!bundle) {
      console.warn(`Bundle with id ${bundleId} not found. Handling increment action without it.`);
      return;
    }

    const valuePropertyName = typeof value !== 'undefined' ? 'bundle_quantity_selected' : 'bundle_quantity';

    return {
      bundle_id: bundle.id,
      bundle_name: bundle.name,
      bundle_price: formatMinorToMajorUnits({ amount: bundle.price.amount, currency: bundle.price.currency }),
      bundle_currency: bundle.price.currency,
      [valuePropertyName]: typeof value !== 'undefined' ? value : 1,
    }
  }

  static getItemData = (id, value, type, props) => {
    if (type === 'ticket') {
      return this.getTicketData(id, value, props);
    }

    if (type === 'product') {
      return this.getProductData(id, value, props);
    }

    if (type === 'bundle') {
      return this.getBundleData(id, value, props);
    }

    return {};
  }

  static getTargetEvent = (eventId, props) => {
    if (props.selectedEvent && Number(props.selectedEvent.event_id) === Number(eventId)) {
      return props.selectedEvent;
    }

    if (props.mainEvent && Number(props.mainEvent.event_id) === Number(eventId)) {
      return props.mainEvent;
    }

    return null;
  }

  static getTargetEventMiscData = (event, type) => {
    const [startDate, startTime] = event.start.split(' ');

    const [endDate, endTime] = event.end ? event.end.split(' ') : [undefined, undefined];

    const endDateAndTime = {
      ticket_event_end_date: endDate,
      ticket_event_end_time: endTime,
    }

    return {
      [`${type}_event_id`]: event.event_id,
      [`${type}_event_name`]: event.name,
      [`${type}_event_start_date`]: startDate,
      [`${type}_event_start_time`]: startTime,
      [`${type}_event_timezone`]: event.timezone,
      ...(endDateAndTime.endDate ? endDateAndTime : {}),
    }
  }

  static getItemQuantityChangeEventName = (type, action) => {
    const itemName = type.charAt(0).toUpperCase() + type.slice(1);

    if (!action) {
      return `${type}QuantitySelector`;
    }

    const prefix = action === 'increment' ? 'add' : 'remove';
    return `${prefix}${itemName}`;
  }

  static getItemQuantityChangeLayer = (type, action, id, value, props) => {
    const dataLayer = this.getBaseDataLayer(props);

    const eventId = this.getEventId(id, type, props);

    const itemData = this.getItemData(id, value, type, props);

    if (!itemData) {
      console.warn(`Item data for id ${id} not found. Handling increment action without it.`);
      return;
    }

    const event = this.getTargetEvent(eventId, props);

    if (!event) {
      console.warn(`Event with id ${eventId} not found. Handling increment action without it.`);
      return;
    }

    const eventData = this.getTargetEventMiscData(event, type);

    dataLayer.event = this.getItemQuantityChangeEventName(type, action);
    dataLayer.ecommerce = {
      [type]: {
        ...itemData,
        ...eventData
      }
    }

    return dataLayer
  }

  static getEventInfoLayer = (props) => {
    const dataLayer = this.getBaseDataLayer(props);

    if(!props.selectedEvent) {
      return;
    }

    const { start, timezone } = props.selectedEvent;

    if (!start) return;

    const { dayName, dayNumber, monthName, year, time } = getDateComponents(new Date(start));
    dataLayer.event = 'eventInfo';
    dataLayer.ecommerce = {
      event_id: props.mainEvent.event_id,
      event_name: props.mainEvent.name,
      event_timezone: timezone,
      event_day: dayName,
      event_date: dayNumber,
      event_month: monthName,
      event_year: year,
      event_time: time,
      event_slug: props.listingSlug,
      source_url: window.location.href,
    };

    return dataLayer;
  }
}
